import React, { useState, useEffect, useRef } from 'react';
import { initializeApp } from 'firebase/app';
import { getAuth, signInAnonymously, signInWithCustomToken, onAuthStateChanged } from 'firebase/auth';
import { getFirestore, doc, setDoc, onSnapshot, updateDoc, collection } from 'firebase/firestore';

// Your actual Firebase project configuration
// IMPORTANT: Replace these values with the ones from YOUR Firebase project settings.
const firebaseConfig = {
    apiKey: "AIzaSyD-2eEs8-bdPdbOx12nRCvzmUxrI2CaQng",
    authDomain: "roboshakti-textbook-linker.firebaseapp.com",
    projectId: "roboshakti-textbook-linker",
    storageBucket: "roboshakti-textbook-linker.firebasestorage.app",
    messagingSenderId: "469829099477",
    appId: "1:469829099477:web:754df1cbeb0e9f5b5ba75c",
    measurementId: "G-559ENY6CVX"
};

// Define a consistent app identifier for Firestore paths for your deployed app.
// This must be a simple string without special characters like colons or slashes.
// Use this EXACT string for both desktop and mobile apps.
const APP_IDENTIFIER = 'roboshakti-teachers-app';

// Since we are building locally for deployment, __initial_auth_token is not available.
// We'll set it to null, and the app will fall back to anonymous sign-in.
const initialAuthToken = null;

// Dynamically load qrious library for QR code generation
// This function will append the script to the document body
const loadScript = (src) => {
    return new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = src;
        script.onload = () => resolve(script);
        script.onerror = () => reject(new Error(`Script load error for ${src}`));
        document.body.appendChild(script);
    });
};

// Main App component for the Desktop Receiver
function App() {
    const [db, setDb] = useState(null);
    const [auth, setAuth] = useState(null);
    const [userId, setUserId] = useState(null); // This is the desktop's user ID
    const [pairingCode, setPairingCode] = useState('');
    const [qrCodeValue, setQrCodeValue] = useState(''); // Value encoded in the QR code (code|userId)
    const [scannedUrl, setScannedUrl] = useState('');
    const [status, setStatus] = useState('Initializing...');
    const [isAuthReady, setIsAuthReady] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [modalMessage, setModalMessage] = useState('');
    const qrCodeCanvasRef = useRef(null); // Ref for the canvas element where QR code is drawn

    // Ref to store the unsubscribe function for the Firestore listener
    const unsubscribeRef = useRef(null);

    // Function to show a custom modal message instead of alert()
    const showCustomModal = (message) => {
        setModalMessage(message);
        setShowModal(true);
    };

    // Function to generate a random 6-character alphanumeric pairing code
    const generatePairingCode = () => {
        return Math.random().toString(36).substring(2, 8).toUpperCase();
    };

    // Effect for Firebase initialization and authentication listener
    useEffect(() => {
        try {
            const app = initializeApp(firebaseConfig);
            const firestoreDb = getFirestore(app);
            const firebaseAuth = getAuth(app);

            setDb(firestoreDb);
            setAuth(firebaseAuth);

            // Listen for auth state changes to get the current user's UID
            const authUnsubscribe = onAuthStateChanged(firebaseAuth, async (user) => {
                if (user) {
                    // User is signed in (anonymously or via custom token)
                    setUserId(user.uid);
                    setStatus('Authenticated. Generating pairing code...');
                } else {
                    // No user signed in, attempt anonymous sign-in
                    try {
                        // Use initialAuthToken if available (from Canvas environment), otherwise sign in anonymously
                        if (typeof initialAuthToken !== 'undefined' && initialAuthToken) {
                            await signInWithCustomToken(firebaseAuth, initialAuthToken);
                        } else {
                            await signInAnonymously(firebaseAuth);
                        }
                    } catch (error) {
                        console.error("Error signing in:", error);
                        setStatus(`Authentication failed: ${error.message}`);
                        showCustomModal(`Authentication failed: ${error.message}`);
                    }
                }
                setIsAuthReady(true); // Mark authentication as ready
            });

            // Cleanup function for this effect: unsubscribe from auth changes
            return () => {
                authUnsubscribe();
                // Also ensure Firestore listener is unsubscribed if it was active
                if (unsubscribeRef.current) {
                    unsubscribeRef.current();
                }
            };
        } catch (error) {
            console.error("Firebase initialization failed:", error);
            setStatus(`Initialization failed: ${error.message}`);
            showCustomModal(`Initialization failed: ${error.message}`);
        }
    }, []); // Empty dependency array means this effect runs once on mount

    // Effect to generate pairing code and QR code value once authenticated and userId is available
    useEffect(() => {
        if (isAuthReady && userId && !pairingCode) {
            const code = generatePairingCode();
            setPairingCode(code);
            // The QR code value now includes both the pairing code and the desktop's userId
            setQrCodeValue(`${code}|${userId}`);
            setStatus(`Generated pairing code: ${code}. Waiting for mobile connection...`);
        }
    }, [isAuthReady, userId, pairingCode]); // Re-run if these dependencies change

    // Effect to set up Firestore listener for incoming scanned URLs
    useEffect(() => {
        if (db && userId && isAuthReady && pairingCode) {
            setStatus(`Ready to receive scans. Pairing Code: ${pairingCode}`);

            // Define the Firestore document path for this user's pairing session.
            // This path is unique per desktop user and pairing code.
            const pairingDocRef = doc(db, `artifacts/${APP_IDENTIFIER}/users/${userId}/pairing_sessions`, pairingCode);

            // Function to initialize or reset the pairing document in Firestore
            const initializePairingDoc = async () => {
                try {
                    await setDoc(pairingDocRef, {
                        scannedUrl: '', // Initialize with an empty URL
                        timestamp: new Date().toISOString(),
                        status: 'waiting',
                        desktopUserId: userId // Store the desktop's user ID in the document
                    }, { merge: true }); // Use merge to avoid overwriting other fields if they exist
                } catch (error) {
                    console.error("Error initializing pairing document:", error);
                    setStatus(`Error initializing pairing: ${error.message}`);
                    showCustomModal(`Error initializing pairing: ${error.message}`);
                }
            };
            initializePairingDoc(); // Call immediately to set up the document

            // Set up real-time listener for changes to this specific pairing document
            unsubscribeRef.current = onSnapshot(pairingDocRef, (docSnap) => {
                if (docSnap.exists()) {
                    const data = docSnap.data();
                    // Check if a new URL has been scanned and it's different from the last one
                    if (data.scannedUrl && data.scannedUrl !== scannedUrl) {
                        setScannedUrl(data.scannedUrl);
                        setStatus(`Opening: ${data.scannedUrl}`);
                        try {
                            // Open the URL in a new tab/window
                            window.open(data.scannedUrl, '_blank');
                            // After opening, clear the scannedUrl in Firestore to allow new scans of the same URL
                            updateDoc(pairingDocRef, { scannedUrl: '', status: 'opened' });
                        } catch (error) {
                            console.error("Error opening URL:", error);
                            setStatus(`Failed to open URL: ${error.message}`);
                            showCustomModal(`Failed to open URL: ${error.message}. Please check browser pop-up blockers.`);
                        }
                    } else if (data.status === 'waiting') {
                        // Update status if it's just waiting for a scan
                        setStatus(`Ready to receive scans. Pairing Code: ${pairingCode}`);
                    }
                } else {
                    // If the document doesn't exist (e.g., deleted by mobile app or manually), re-initialize
                    setStatus(`Pairing document not found. Re-initializing...`);
                    initializePairingDoc();
                }
            }, (error) => {
                console.error("Error listening to pairing document:", error);
                setStatus(`Real-time updates failed: ${error.message}`);
                showCustomModal(`Real-time updates failed: ${error.message}`);
            });

            // Cleanup function for this effect: unsubscribe from Firestore changes
            return () => {
                if (unsubscribeRef.current) {
                    unsubscribeRef.current();
                    unsubscribeRef.current = null;
                }
            };
        }
    }, [db, userId, isAuthReady, pairingCode, scannedUrl]); // Re-run if these dependencies change

    // Effect to load qrious.js library and draw the QR code onto the canvas
    useEffect(() => {
        if (qrCodeValue && qrCodeCanvasRef.current) { // Only proceed if QR code value and canvas ref are available
            loadScript('https://unpkg.com/qrious@4.0.2/dist/qrious.min.js')
                .then(() => {
                    // Ensure the canvas element is ready to be drawn on
                    const canvas = document.createElement('canvas');
                    canvas.width = 256;
                    canvas.height = 256;
                    // Clear any previous content in the ref's current element
                    qrCodeCanvasRef.current.innerHTML = '';
                    // Append the new canvas element
                    qrCodeCanvasRef.current.appendChild(canvas);

                    // Create and draw the QR code using QRious
                    new window.QRious({
                        element: canvas,
                        value: qrCodeValue, // The data to encode in the QR code (code|userId)
                        size: 256,
                        level: 'H' // High error correction level for better scannability
                    });
                })
                .catch(error => {
                    console.error("Failed to load qrious.js or generate QR code:", error);
                    showCustomModal(`Failed to load QR code generator: ${error.message}`);
                });
        }
    }, [qrCodeValue]); // This effect re-runs when qrCodeValue changes

    // Render the UI for the Desktop Receiver
    return (
        <div className="min-h-screen bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center p-4">
            <div className="bg-white rounded-xl shadow-2xl p-8 md:p-12 max-w-xl w-full text-center transform transition-all duration-300 hover:scale-105">
                <h1 className="text-4xl font-extrabold text-indigo-800 mb-4 tracking-tight">
                    Roboshakti Desktop Receiver
                </h1>
                <p className="text-lg text-gray-600 mb-8">
                    Your gateway to seamless textbook content access.
                </p>

                {userId && (
                    <p className="text-sm text-gray-500 mb-4">
                        Your User ID: <span className="font-mono text-gray-700 break-all">{userId}</span>
                    </p>
                )}

                <div className="mb-8">
                    {pairingCode ? (
                        <>
                            <p className="text-2xl font-bold text-gray-800 mb-4">
                                Scan this QR Code with your Roboshakti Mobile Scanner:
                            </p>
                            <div className="bg-gray-100 p-4 rounded-lg inline-block shadow-inner">
                                {/* This div will contain the canvas element generated by qrious.js */}
                                <div ref={qrCodeCanvasRef} className="rounded-md" style={{ width: 256, height: 256 }}></div>
                            </div>
                            <p className="text-xl font-mono text-indigo-700 mt-4">
                                Or manually enter: <span className="font-extrabold">{qrCodeValue}</span> {/* Display the full QR value */}
                            </p>
                        </>
                    ) : (
                        <p className="text-gray-700">Generating pairing code...</p>
                    )}
                </div>

                <div className="bg-blue-100 border-l-4 border-blue-500 text-blue-700 p-4 rounded-lg mb-6" role="alert">
                    <p className="font-bold">Status:</p>
                    <p>{status}</p>
                </div>

                {scannedUrl && (
                    <div className="bg-green-100 border-l-4 border-green-500 text-green-700 p-4 rounded-lg mt-6" role="alert">
                        <p className="font-bold">Last Scanned URL:</p>
                        <p className="break-all">{scannedUrl}</p>
                    </div>
                )}

                {/* Custom Modal for alerts */}
                {showModal && (
                    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
                        <div className="bg-white rounded-lg p-6 shadow-xl max-w-sm w-full text-center">
                            <h3 className="text-xl font-bold text-red-600 mb-4">Error / Information</h3>
                            <p className="text-gray-700 mb-6">{modalMessage}</p>
                            <button
                                onClick={() => setShowModal(false)}
                                className="bg-red-500 hover:bg-red-600 text-white font-bold py-2 px-4 rounded-lg transition duration-300 ease-in-out"
                            >
                                Close
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

export default App;
